import Highcharts, {
  Chart,
  SeriesOptionsType,
  XrangePointOptionsObject,
  YAxisOptions,
} from "highcharts";
import { TimeseriesData } from "../core/models/TimeSeriesData";
import { APP_PRIMARY_COLOR, APP_WHITE, CHART_PRIMARY_COLOR } from "../RTUThemeProvider";
import { castNumberTo2Decimals, formatValue } from "./utility";
import { isNumberValid } from "./utility";
import truckSVG from "../assets/icons/TruckIcon/truck_icon.svg";
import truckSVGWhite from "../assets/icons/TruckIcon/truck_icon_white.svg";
import max from "lodash/max";
import min from "lodash/min";
import { format } from "date-fns";
import { calculateLevelPercentage } from "./installationTankHelpers";
import { consumptionData } from "../core/models/ConsumptionData";
import { formatDateTime, getDateFromTimezonePtBrToGMT } from "./datetime";
import { EMPTY_STRING_LABEL } from "../core/constants/appGlobals";
import { ILevels } from "../core/contracts/IConsumption";

export type chartThresholds = {
  id: string;
  value: number;
  color: React.CSSProperties["color"];
  HTMLLabel?: string;
};

export function buildTimeAreaChartOptions(
  data: TimeseriesData[],
  title: string,
  seriesName: string,
  yTitle?: string,
  yThresholds?: chartThresholds[],
  xThresholds?: chartThresholds[],
  decorateYTitle = false,
  hideHoursFromTooltip = false,
  useNormalizedData?: boolean,
  levels?: Record<string, Record<string, ILevels | undefined>[]>[],
  dataSerie2?: TimeseriesData[] | undefined,
  serie2Name?: string,
  y2Title?: string,
  isDark?: boolean,
  addTimezone?: boolean
): Highcharts.Options {
  function getPointCategoryName(point: string) {
    const data = new Date(point);
    const currentHour = data.getHours();
    const currentSecond = data.getSeconds();
    if (currentHour >= 0 && currentHour < 6) {
      return "0";
    } else if (currentHour >= 6 && currentHour < 12) {
      return "6";
    } else if (currentHour >= 12 && currentHour < 18) {
      return "12";
    } else if (currentHour >= 18 && currentHour <= 23) {
      return "18";
    }
    return (currentHour as unknown) as string;
  }
  const forcedSortData = data
    ?.map((d: TimeseriesData) => [
      addTimezone ? d[0].getTime() : d[0].getTime() + d[0].getTimezoneOffset() * 60000,
      castNumberTo2Decimals(d[1]),
    ])
    ?.sort((a, b) => {
      return a[0] - b[0];
    });

  const isNotEverybodySerie2IsZero = dataSerie2?.some((e) => e[1] != 0);
  const forcedSortData2 = dataSerie2
    ?.map((d: TimeseriesData) => [
      addTimezone ? d[0].getTime() : d[0].getTime() + d[0].getTimezoneOffset() * 60000,
      castNumberTo2Decimals(d[1]),
    ])
    ?.sort((a, b) => {
      return a[0] - b[0];
    });

  const minValueFromSeries = min(forcedSortData.map((x) => x[1]));
  const maxValueFromSeries = max(forcedSortData.map((x) => x[1]));
  const minValueFromSeries2 = forcedSortData2 && min(forcedSortData2.map((x) => x[1]));
  const maxValueFromSeries2 = forcedSortData2 && max(forcedSortData2.map((x) => x[1]));

  const valuesSeries = [
    {
      min: minValueFromSeries || 0,
      max: maxValueFromSeries || 0,
    },
    {
      min: minValueFromSeries2 || 0,
      max: maxValueFromSeries2 || 0,
    },
  ];

  let useSeries: SeriesOptionsType[] =
    dataSerie2?.length && isNotEverybodySerie2IsZero
      ? [
        {
          name: seriesName,
          type: "area",
          data: forcedSortData,
          tooltip: levels
            ? {
              pointFormat:
                '<span style="color:red">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
              pointFormatter: function () {
                if (this.options.x) {
                  const level = (levels[this.options.x] as unknown) as Record<
                    string,
                    ILevels | undefined
                  >;

                  if (level) {
                    const keys = Object.keys(level);
                    this.color = keys.length < 4 ? "red" : this.color;
                    const entries = ["<br/>"].concat(
                      keys.map((key: string) => {
                        return `<div style="display:inline;"><span style="width:100px">${getNameFromCategory(
                          getPointCategoryName(key)
                        )}:</span><span><b> ${(useNormalizedData
                          ? level[key]?.level
                          : level[key]?.raw_level
                        )?.toFixed(2)}</b></span></div><br/>`;
                      })
                    );
                    entries.push("<hr/>");
                    entries.push(
                      `<span style='color:${this.color}; white-space:pre;'>●</span> ${this.series.name
                      }<b>: ${this.options.y?.toFixed(2)}</b><br/>`
                    );
                    return entries.join("");
                  }
                }
                return "";
              },
            }
            : undefined,
        },
        {
          name: serie2Name,
          type: "line",
          data: forcedSortData2,
          yAxis: 1,
          tooltip: levels
            ? {
              pointFormat:
                '<span style="color:red">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
              pointFormatter: function () {
                if (this.options.x) {
                  const level = (levels[this.options.x] as unknown) as Record<
                    string,
                    ILevels | undefined
                  >;
                  if (level) {
                    const keys = Object.keys(level);
                    this.color = keys.length < 4 ? "red" : this.color;
                    const entries = ["<br/>"].concat(
                      keys.map((key: string) => {
                        return `<div style="display:inline;"><span style="width:100px">${getNameFromCategory(
                          getPointCategoryName(key)
                        )}:</span><span><b> ${(useNormalizedData
                          ? level[key]?.level
                          : level[key]?.raw_level
                        )?.toFixed(2)}</b></span></div><br/>`;
                      })
                    );
                    entries.push("<hr/>");
                    entries.push(
                      `<span style='color:${this.color}; white-space:pre;'>●</span> ${this.series.name
                      }<b>: ${this.options.y?.toFixed(2)}</b><br/>`
                    );
                    return entries.join("");
                  }
                }
                return "";
              },
            }
            : undefined,
        },
      ]
      : [
        {
          name: seriesName,
          type: "area",
          data: forcedSortData,
          tooltip: levels
            ? {
              pointFormat:
                '<span style="color:red">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
              pointFormatter: function () {
                if (this.options.x) {
                  let level = (levels[this.options.x] as unknown) as Record<
                    string,
                    ILevels | undefined
                  >;

                  if (level) {
                    const auxKeys = Object.keys(level);

                    if (auxKeys.length == 4) {
                      const newLevel = {
                        [auxKeys[0]]: level[auxKeys[0]],
                        [auxKeys[1]]: level[auxKeys[1]],
                        [auxKeys[2]]: level[auxKeys[2]],
                        [auxKeys[3]]: level[auxKeys[3]],
                      } as Record<string, ILevels | undefined>;
                      level = newLevel;
                    }
                    const keys = Object.keys(level);
                    this.color = keys.length < 4 ? "red" : this.color;
                    const entries = ["<br/>"].concat(
                      keys.map((key: string) => {
                        return `<div style="display:inline;"><span style="width:100px">${getNameFromCategory(
                          getPointCategoryName(key)
                        )}:</span><span><b> ${(useNormalizedData
                          ? level[key]?.level
                          : level[key]?.raw_level
                        )?.toFixed(2)}</b></span></div><br/>`;
                      })
                    );
                    entries.push("<hr/>");
                    entries.push(
                      `<span style='color:${this.color}; white-space:pre;'>●</span> ${this.series.name
                      }<b>: ${this.options.y?.toFixed(2)}</b><br/>`
                    );
                    return entries.join("");
                  }
                }
                return "";
              },
            }
            : undefined,
        },
      ];

  if (xThresholds) {
    if (isNumberValid(valuesSeries[0].max)) {
      useSeries = [
        ...useSeries,
        {
          name: "Entregas",
          type: "scatter",
          data: xThresholds
            .map((x) => {
              if (!x.value) {
                return null;
              }

              return {
                x: x.value,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                y: valuesSeries[0].max! * 1.05, // add 5% upper from maxValue from series
                custom: {
                  tooltip: `<div> <span><b>Data:</b></span> <span>${format(
                    new Date(x.value),
                    "dd MMM yyyy"
                  )}</span> </div><br/> <div> <span><b>Hora:</b></span> <span>${format(
                    new Date(x.value),
                    "HH:mm"
                  )}</span> </div><br/>`,
                },
              };
            })
            .filter((x) => x),
          marker: {
            fillColor: "#fff",
            symbol: isDark ? `url(${truckSVGWhite})` : `url(${truckSVG})`,
          },
        },
      ];
    }
  }

  let ret: Highcharts.Options = {
    title: {
      text: title,
    },
    xAxis: {
      type: "datetime",
      crosshair: true,
      labels: {
        style: {
          color: isDark ? "#FFF" : "#666666",
        },
      },
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, isDark ? CHART_PRIMARY_COLOR : CHART_PRIMARY_COLOR],
            [1, isDark ? "#7cb5ec00" : "#7cb5ec00"],
          ],
        },
      },
      scatter: {
        tooltip: {
          pointFormat: "{point.custom.tooltip}",
        },
        color: isDark ? "#fff" : "#000",
      },
    },
    series: useSeries,
    legend: {
      useHTML: true,
      labelFormatter: function () {
        return '<div style="margin-left: 8px">' + this.name + "</div>";
      },
    },
  };

  if (hideHoursFromTooltip) {
    ret = { ...ret, tooltip: { xDateFormat: "%A, %b %e" } };
  }

  if (yTitle) {
    ret = {
      ...ret,
      chart: {
        spacingTop: 56,
      },
      yAxis:
        y2Title && isNotEverybodySerie2IsZero
          ? [
            {
              labels: {
                style: {
                  color: isDark ? "#FFF" : "#666666",
                },
              },
              title: {
                useHTML: true,
                // align: "high",
                // offset: 0,
                text: yTitle,
                // rotation: 0,
                // y: decorateYTitle ? -24 : -10,
                // x: decorateYTitle ? 24 : 0,
                style: decorateYTitle
                  ? {
                    color: APP_PRIMARY_COLOR,
                    fontSize: "large",
                  }
                  : {
                    color: isDark ? "#FFF" : "#666666",
                  },
              },
            },
            {
              labels: {
                style: {
                  color: isDark ? "#FFF" : "#666666",
                },
              },
              title: {
                useHTML: true,
                // align: "high",
                // offset: 0,
                text: y2Title,
                // rotation: 0,
                // y: decorateYTitle ? -14 : -10,
                // x: decorateYTitle ? -84 : 0,
                style: decorateYTitle
                  ? {
                    color: APP_PRIMARY_COLOR,
                    fontSize: "large",
                  }
                  : {
                    color: "#fff",
                  },
              },
              opposite: true,
            },
          ]
          : [
            {
              labels: {
                style: {
                  color: isDark ? "#FFF" : "#666666",
                },
              },
              title: {
                useHTML: true,
                align: "high",
                offset: 0,
                text: yTitle,
                rotation: 0,
                y: decorateYTitle ? -24 : -10,
                x: decorateYTitle ? 24 : 0,
                style: decorateYTitle
                  ? {
                    color: APP_PRIMARY_COLOR,
                    fontSize: "large",
                  }
                  : {
                    color: isDark ? "#FFF" : "#666666",
                  },
              },
            },
          ],
    };
  }

  if (yThresholds) {
    const values = [yThresholds.map((x) => x.value), [0]];

    ret = {
      ...ret,
      yAxis: ((ret.yAxis || []) as YAxisOptions[]).map((e, index) => {
        return valuesSeries[index].max != 0
          ? {
            ...e,
            plotLines: (!dataSerie2?.length ? index == 0 && isNotEverybodySerie2IsZero : true)
              ? yThresholds.map((x) => {
                return {
                  id: x.id,
                  color: x.color,
                  value: castNumberTo2Decimals(x.value),
                  dashStyle: "Dash",
                  label: {
                    text: x.HTMLLabel,
                    style: { color: x.color },
                    useHTML: true,
                  },
                  zIndex: 500,
                };
              })
              : undefined,
            min: min([valuesSeries[index].min, ...values[index]]),
            max: valuesSeries[index].max
              ? max([valuesSeries[index]?.max * 1.05, ...values[index]])
              : max(values[index]),
          }
          : {};
      }),
    };
  }

  if (xThresholds) {
    ret = {
      ...ret,
      xAxis: {
        ...ret.xAxis,
        plotLines: xThresholds.map((x) => {
          return {
            id: x.id,
            color: x.color,
            value: x.value,
            dashStyle: "Dash",
            label: {
              text: x.HTMLLabel,
              style: { color: x.color },
              useHTML: true,
            },
            zIndex: 500,
          };
        }),
      },
    };
  }

  return ret;
}

export function buildHTMLLabelForThreshold(
  mainLabel: string,
  value?: number,
  uom = "",
  breakLabelValue = true
) {
  return `<strong>${mainLabel}</strong> ${breakLabelValue ? "<br/>" : ""} ${isNumberValid(value) ? `(${formatValue(value)}${uom ? ` ${uom}` : ""})` : ""
    }`;
}

export function buildColoredAxisTitle(title: string) {
  return `<strong>${title}</strong>`;
}

export function buildHeatMapChartOptions(
  xCategories: string[],
  yCategories: string[],
  data: number[][],
  title?: string,
  seriesName?: string,
  isDark?: boolean
): Highcharts.Options {
  function getPointCategoryName(point: Highcharts.Point, dimension: "x" | "y") {
    const series = point.series,
      isY = dimension === "y",
      axis = series[isY ? "yAxis" : "xAxis"];
    return axis.categories[point[dimension] as number];
  }

  const formattedData = data
    ?.map((x) => [x[0], x[1], castNumberTo2Decimals(x[2])])
    ?.sort((a, b) => Number(`${a[0]}${a[1]}`) - Number(`${b[0]}${b[1]}`));

  return {
    chart: {
      type: "heatmap",
    },

    title: {
      text: title,
      useHTML: true,
      style: {
        color: isDark ? "#FFF" : "#666666",
      },
    },

    xAxis: {
      type: "category",
      categories: xCategories,

      labels: {
        style: {
          color: isDark ? "#FFF" : "#666666",
        },
        formatter: function () {
          return getNameFromCategory(this.value.toString(), xCategories.length === 1);
        },
      },
    },

    yAxis: {
      type: "category",
      categories: yCategories,
      reversed: true,
      labels: {
        style: {
          color: isDark ? "#FFF" : "#666666",
        },
      },
      title: {
        text: "",
        style: {
          color: "#fff",
        },
      },
    },

    colorAxis: {
      min: 0,
      minColor: "#FFFFFF",
      maxColor: CHART_PRIMARY_COLOR,
    },

    legend: {
      align: "right",
      layout: "vertical",
      margin: 0,
      verticalAlign: "top",
      title: {
        style: {
          color: "#fff",
        },
      },
      itemStyle: {
        color: isDark ? "#FFF" : "#666666",
      },
    },

    tooltip: {
      formatter: function () {
        return `${getNameFromCategory(getPointCategoryName(this.point, "y"))}${xCategories.length > 1
            ? ` ${getNameFromCategory(getPointCategoryName(this.point, "x"))}`
            : ""
          }:<br/>
          Média: <b> 
          ${formatValue(this.point.value, 2)}
          </b>`;
      },
    },

    series: [
      {
        type: "heatmap",
        name: seriesName,
        borderWidth: 1,
        data: formattedData,
        dataLabels: {
          enabled: true,
          color: "#000000",
          style: { fontSize: "1rem" },
          formatter: function () {
            const totalData = this.series.data.reduce((prev, curr) => prev + (curr?.value ?? 0), 0);
            return `${this.point.value ? calculateLevelPercentage(this.point.value, totalData) : "-"
              }%`;
          },
        },
      },
    ],
  };
}

function getNameFromCategory(category: string, onlyOneCategory = false) {
  if (onlyOneCategory && category === "0") {
    return "0-24h";
  }

  switch (category) {
    case "0":
      return "00-06h";
    case "6":
      return "06-12h";
    case "12":
      return "12-18h";
    case "18":
      return "18-24h";
    default:
      return category;
  }
}

type HighchartsBoxPlotPoint = Highcharts.Point & { q1?: number; q3?: number; median?: number };

export function buildBoxPlotChartOptions(
  data: number[][],
  xCategories: string[],
  outliers?: (number | consumptionData)[][],
  seriesName?: string,
  uom?: string,
  title?: string,
  isDark?: boolean
): Highcharts.Options {
  const formatData = data.map((x) => x.map((y) => castNumberTo2Decimals(y)));

  const getDateFromOutlier = (pointIndex: number) => {
    if (!outliers) {
      return EMPTY_STRING_LABEL;
    }

    const pointData = outliers[pointIndex][1] as consumptionData;

    return formatDateTime(pointData.date);
  };

  const useData: SeriesOptionsType[] = [
    {
      type: "boxplot",
      name: seriesName,
      data: formatData,
      tooltip: {
        pointFormatter: function () {
          return `Período: <b>${getNameFromCategory(
            this.category,
            xCategories.length === 1
          )}</b><br/><span style='color:${this.color}'>●</span> <b> ${this.series.name
            }</b><br/>Máximo: ${this.high}<br/>Quartil Superior: ${(this as HighchartsBoxPlotPoint).q3
            }<br/>Mediana: ${(this as HighchartsBoxPlotPoint).median}<br/>Quartil Inferior: ${(this as HighchartsBoxPlotPoint).q1
            }<br/>Mínimo: ${this.low}<br/>`;
        },
      },
    },
  ];

  if (outliers) {
    const formatOutliers = outliers.map((x) => [
      x[0],
      castNumberTo2Decimals((x[1] as consumptionData)?.level),
    ]);
    useData.push({
      name: "Outliers",
      color: isDark ? CHART_PRIMARY_COLOR : CHART_PRIMARY_COLOR,
      type: "scatter",
      data: formatOutliers,
      marker: {
        fillColor: APP_WHITE,
        lineWidth: 2,
        lineColor: isDark ? CHART_PRIMARY_COLOR : CHART_PRIMARY_COLOR,
      },
      tooltip: {
        pointFormatter: function () {
          return `Período: <b>${getNameFromCategory(
            this.category,
            xCategories.length === 1
          )}</b><br/>y: <b>${formatValue(this.y, 2)}${uom ? ` ${uom}` : ""}</b><br/>
          Instante: ${getDateFromOutlier(this.index)}`;
        },
      },
    });
  }

  return {
    chart: {
      type: "boxplot",
    },

    title: {
      text: title,
    },

    xAxis: {
      type: "category",
      categories: xCategories,
      labels: {
        style: {
          color: isDark ? "#FFF" : "#666666",
        },
        formatter: function () {
          return getNameFromCategory(this.value.toString(), xCategories.length === 1);
        },
      },
    },

    yAxis: {
      labels: {
        style: {
          color: isDark ? "#FFF" : "#666666",
        },
      },
      title: {
        text: "",
      },
    },

    plotOptions: {
      boxplot: {
        fillColor: isDark ? CHART_PRIMARY_COLOR : CHART_PRIMARY_COLOR,
        medianColor: APP_WHITE,
        boxDashStyle: undefined,
      },
    },

    series: useData,
  };
}

export function buildGaugePlot(data: number[], isDark?: boolean): Highcharts.Options {
  return {
    chart: {
      type: "solidgauge",
      width: 200,
    },

    title: {
      text: "",
      style: {
        display: "none",
      },
    },
    subtitle: {
      text: "",
      style: {
        display: "none",
      },
    },

    pane: {
      center: ["50%", "85%"],
      size: "100%",
      startAngle: -90,
      endAngle: 90,
      background: [
        {
          backgroundColor: isDark ? "rgb(48, 48, 48)" : "#EEE",
          borderColor: isDark ? "rgb(48, 48, 48)" : "#EEE",
          innerRadius: "60%",
          outerRadius: "100%",
          shape: "arc",
        },
      ],
    },

    exporting: {
      enabled: false,
    },

    tooltip: {
      enabled: false,
    },

    // the value axis
    yAxis: {
      stops: [
        [0.2999, "#55BF3B"], // green
        [0.3, "#DDDF0D"], // yellow
        [0.6999, "#DDDF0D"], // yellow
        [0.7, "#DF5353"], // red
      ],
      lineWidth: 0,
      tickWidth: 0,
      minorTickInterval: null,
      tickAmount: 2,
      title: {
        y: -70,
      },
      labels: {
        y: 16,
      },
      min: 0,
      max: 100,
      plotLines: [
        {
          value: 30,
          zIndex: 5,
          width: 1,
          color: isDark ? "#fff" : "#000000",
          label: {
            style: {
              color: isDark ? "#fff" : "#000000",
            },
            text: "30%",
            x: -10,
            y: -10,
          },
        },
        {
          value: 70,
          zIndex: 5,
          width: 1,
          color: isDark ? "#fff" : "#000000",
          label: {
            style: {
              color: isDark ? "#fff" : "#000000",
            },
            text: "70%",
            x: 25,
            y: -6,
          },
        },
      ],
    },

    plotOptions: {
      solidgauge: {
        dataLabels: {
          y: 5,
          borderWidth: 0,
          useHTML: true,
          color: isDark ? "#fff" : "#000",
        },
      },
    },
    credits: {
      enabled: false,
    },

    series: [
      {
        type: "solidgauge",
        data: data as XrangePointOptionsObject[],
        dataLabels: {
          format:
            '<div style="text-align:center">' +
            '<span style="font-size:25px">{y} %</span><br/>' +
            "</div>",
        },
        tooltip: {
          valueSuffix: " %",
        },
      },
    ],
  };
}
